import io from 'socket.io-client';
import { ConversationMessage, Conversation, ConversationSummary } from './conversationService';
import { logMessage } from '../utils/logMessage';

export interface ChatMessage {
  message: string;
  timestamp: string;
  sender: 'user' | 'ai';
}

export interface EnhancedChatMessage extends ChatMessage {
  conversation_id?: string;
  model?: string;
  usage?: {
    total_tokens: number;
    prompt_tokens: number;
    completion_tokens: number;
  };
  audio_data?: string;
}

class SocketServiceImpl {
  public socket: ReturnType<typeof io> | null = null;
  private readonly serverUrl: string;
  private authToken: string | null = null;

  constructor() {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    if (!backendUrl) {
      throw new Error('REACT_APP_BACKEND_URL environment variable is required');
    }
    this.serverUrl = backendUrl;
  }

  setAuthToken(token: string | null): void {
    this.authToken = token;
  }

  connect(): void {
    if (this.socket) {
      // Reuse the same socket instance so app listeners survive transient reconnects.
      if (this.authToken) {
        (this.socket as any).auth = { token: this.authToken };
      }
      if (!this.socket.connected) {
        this.socket.connect();
      }
      return;
    }

    const connectionOptions: any = {
      transports: ['websocket', 'polling'],
      timeout: 20000,
    };

    // Add auth token if available
    let serverUrl = this.serverUrl;
    if (this.authToken) {
      connectionOptions.auth = { token: this.authToken };
    } else {
      logMessage('[SocketService] No auth token available for connection', 'info');
    }
    this.socket = io(serverUrl, connectionOptions);

    this.socket.on('connect', () => {
      logMessage(
        '[SocketService: connect] Connected to server with SID: ' + this.socket?.id, 
        'info',);
    });

    this.socket.on('disconnect', (reason: string) => {
      logMessage('[SocketService: disconnect] Disconnected from server. Reason: ' + reason, 'info');
    });

    this.socket.on('connect_error', (error: Error) => {
      logMessage('[SocketService: connection_error] Connection error: ' + error, 'error');
    });
  }

  disconnect(): void {
    if (this.socket) {
      logMessage('[SocketService: disconnect] Removing internal listeners and disconnecting...', 'info');
      this.socket.off('connect');
      this.socket.off('disconnect');
      this.socket.off('connect_error');
      this.socket.disconnect();
      this.socket = null;
    }
  }

  // Send message to conversation
  sendMessage(message: string, conversationId?: string): void {
    if (!this.socket?.connected) {
      logMessage('[SocketService: chat_message] Socket not connected', 'info');
      return;
    }
    const messageData = {
      message,
      timestamp: new Date().toISOString(),
      conversation_id: conversationId,
    };
    logMessage(
      '[SocketService: chat_message] Sending message for ai response', 
      'info'
    );
    this.socket.emit('chat_message', messageData);
  }

  // Send message to conversation with TTS
  sendMessageWithTts(message: string, conversationId: string | null, ttsEnabled: boolean, assessmentId?: string): void {
    if (!this.socket?.connected) {
      logMessage('[SocketService: chat_message] Socket not connected', 'info');
      return;
    }
    const messageData = {
      message,
      timestamp: new Date().toISOString(),
      conversation_id: conversationId,
      tts_enabled: ttsEnabled,
      assessment_id: assessmentId,
    };
    logMessage(
      '[SocketService: chat_message] Sending message for ai response', 
      'info'
    );
    this.socket.emit('chat_message', messageData);
  }

  saveTranscript(transcript: string, conversationId: string | null, ttsEnabled: boolean): void {
    if (!this.socket?.connected) {
      console.error('Socket not connected');
      return;
    }
    const transcriptData = {
      transcript,
      conversation_id: conversationId,
      tts_enabled: ttsEnabled,
    };
    logMessage(
      '[SocketService: save_transcript] Saving transcript', 
      'info'
    );
    this.socket.emit('save_transcript', transcriptData);
  }

  gradeTranscript(
    transcript: { speaker: string; text: string }[],
    conversationId: string | null,
    assessment_id?: string,
    dateCompleted?: string,
    assignment_id?: number,
  ): void {
    if (!this.socket?.connected) {
      console.error('Socket not connected');
      return;
    }
    const gradingData = {
      transcript,
      conversation_id: conversationId,
      assessment_id: assessment_id,
      date_completed: dateCompleted,
      assignment_id,
    };
    logMessage(
      '[SocketService: grade_transcript] Grading transcript', 
      'info'
    );
    this.socket.emit('grade_transcript', gradingData);
  }

  createConversation(title?: string, systemPrompt?: string, userId?: string, personaId?: string, learningGoalId?: string, assessmentId?: string): void {
    if (!this.socket?.connected) {
      console.error('Socket not connected');
      return;
    }
    logMessage('[SocketService: create_conversation] Emitting create_conversation event', 'info');
    this.socket.emit('create_conversation', {
      title,
      system_prompt: systemPrompt,
      user_id: userId,
      persona_id: personaId,
      learning_goal_id: learningGoalId,
      assessment_id: assessmentId,
    });
  }

  checkGoals(assessmentId: string, conversationId: string): void {
    if (!this.socket?.connected) {
      console.error('Socket not connected');
      return;
    }
    this.socket.emit('check_goals', {
      assessment_id: assessmentId,
      conversation_id: conversationId,
    });
  }

  listConversations(userId?: string): void {
    if (!this.socket?.connected) {
      console.error('Socket not connected');
      return;
    }
    this.socket.emit('list_conversations', { user_id: userId });
  }

  getConversation(conversationId: string): void {
    if (!this.socket?.connected) {
      console.error('Socket not connected');
      return;
    }
    this.socket.emit('get_conversation', { conversation_id: conversationId });
  }

  // Event listeners - these remove existing listeners before adding new ones
  onConnectionResponse(callback: (data: any) => void): void {
    this.socket?.off('connection_response', callback);
    this.socket?.on('connection_response', callback);
  }
  onAIResponse(callback: (data: EnhancedChatMessage) => void): void {
    this.socket?.off('ai_response', callback);
    this.socket?.on('ai_response', callback);
  }
  onConversationCreated(callback: (data: { conversation_id: string; title: string; created_at: string }) => void): void {
    this.socket?.off('conversation_created', callback as any);
    this.socket?.on('conversation_created', callback as any);
  }
  onConversationsList(callback: (data: { conversations: ConversationSummary[] }) => void): void {
    this.socket?.off('conversations_list', callback);
    this.socket?.on('conversations_list', callback);
  }
  onConversationData(callback: (data: Conversation & { messages: ConversationMessage[] }) => void): void {
    this.socket?.off('conversation_data', callback);
    this.socket?.on('conversation_data', callback);
  }
  onConversationUpdated(callback: (data: Conversation & { messages: ConversationMessage[] }) => void): void {
    this.socket?.off('conversation_updated', callback);
    this.socket?.on('conversation_updated', callback);
  }
  onTranscriptSaved(callback: (data: { message: string; transcript: string; message_id: string; conversation_id: string }) => void): void {
    this.socket?.off('transcript_saved', callback);
    this.socket?.on('transcript_saved', callback);
  }
  onGradingStarted(callback: (data: { status: string }) => void): void {
    this.socket?.off('grading_started', callback);
    this.socket?.on('grading_started', callback);
  }
  onGradingProgress(callback: (data: { status: string }) => void): void {
    this.socket?.off('grading_progress', callback);
    this.socket?.on('grading_progress', callback);
  }
  onGradingComplete(callback: (data: { transcript_file: string; grading_file: string; grading_result: string, result_id: string, transcript_id: number }) => void): void {
    this.socket?.off('grading_complete', callback);
    this.socket?.on('grading_complete', callback);
  }
  onGradingError(callback: (error: { message: string }) => void): void {
    this.socket?.off('grading_error', callback);
    this.socket?.on('grading_error', callback);
  }
  onError(callback: (error: { message: string; code?: string }) => void): void {
    this.socket?.off('error', callback);
    this.socket?.on('error', callback);
  }
  onSessionCancled(callback: (error: { message: string; code?: string }) => void): void {
    this.socket?.off('session_cancled', callback);
    this.socket?.on('session_cancled', callback);
  }

  onConnectionError(callback: (error: { message: string; code?: string }) => void): void {
    this.socket?.off('connection_error', callback as any);
    this.socket?.on('connection_error', callback);
  }

  // ---- THE IMPORTANT PART: Remove ALL listeners at once ----
  offAllListeners(): void {
    if (!this.socket) return;
    this.socket.off('connection_response');
    this.socket.off('ai_response');
    this.socket.off('conversation_created');
    this.socket.off('conversations_list');
    this.socket.off('conversation_data');
    this.socket.off('conversation_updated');
    this.socket.off('transcript_saved');
    this.socket.off('grading_started');
    this.socket.off('grading_progress');
    this.socket.off('grading_complete');
    this.socket.off('grading_error');
    this.socket.off('error');
    this.socket.off('session_cancled');
    this.socket.off('connection_error');
  }

  isConnected(): boolean {
    return this.socket?.connected || false;
  }
}

const socketService = new SocketServiceImpl();
export default socketService;